// web/src/utils/permissions.js

export const normalizePerm = (perm) => {
  if (!perm) return "";

  return String(perm)
    .trim()
    .toLowerCase();
};

export const isGlobalAdminUser = (user) => {
  if (!user) return false;
  return user?.isGlobalAdmin === true;
};

export const getUserPerms = (user) => {
  if (!user) return [];

  if (isGlobalAdminUser(user)) {
    return ["*"];
  }

  return Array.from(
    new Set(
      (user?.permissions || [])
        .map((perm) => normalizePerm(perm))
        .filter(Boolean)
    )
  );
};

export const hasPermission = (user, permission) => {
  if (!user || !permission) return false;

  if (isGlobalAdminUser(user)) {
    return true;
  }

  const perms = getUserPerms(user);
  const normalized = normalizePerm(permission);

  return perms.includes("*") || perms.includes(normalized);
};

export const hasPermUser = (user, permission) => {
  return hasPermission(user, permission);
};

export const hasAnyPermission = (user, permissions = []) => {
  if (!user) return false;

  if (isGlobalAdminUser(user)) {
    return true;
  }

  const perms = getUserPerms(user);
  if (perms.includes("*")) return true;

  const normalizedList = (permissions || [])
    .map((perm) => normalizePerm(perm))
    .filter(Boolean);

  return normalizedList.some((perm) => perms.includes(perm));
};

export const hasAllPermissions = (user, permissions = []) => {
  if (!user) return false;

  if (isGlobalAdminUser(user)) {
    return true;
  }

  const perms = getUserPerms(user);
  if (perms.includes("*")) return true;

  const normalizedList = (permissions || [])
    .map((perm) => normalizePerm(perm))
    .filter(Boolean);

  return normalizedList.every((perm) => perms.includes(perm));
};

export const getUserModules = (user) => {
  if (!user) return [];

  if (isGlobalAdminUser(user)) {
    return [];
  }

  return Array.from(
    new Set(
      (user?.modules || [])
        .map((moduleCode) => String(moduleCode).trim().toUpperCase())
        .filter(Boolean)
    )
  );
};

export const hasModule = (user, moduleCode) => {
  if (!user || !moduleCode) return false;

  if (isGlobalAdminUser(user)) {
    return true;
  }

  const modules = getUserModules(user);
  return modules.includes(String(moduleCode).trim().toUpperCase());
};

const getItemMeta = (item) => item?.meta || {};

const getRequiredModule = (item) => {
  const meta = getItemMeta(item);

  return (
    item?.module ||
    item?.moduleCode ||
    item?.requiredModule ||
    item?.feature ||
    meta?.module ||
    meta?.moduleCode ||
    meta?.requiredModule ||
    meta?.feature ||
    null
  );
};

const getRequiredPermission = (item) => {
  const meta = getItemMeta(item);

  return (
    item?.permission ||
    item?.perm ||
    item?.authority ||
    meta?.permission ||
    meta?.perm ||
    meta?.authority ||
    null
  );
};

const getRequiredPermissions = (item) => {
  const meta = getItemMeta(item);

  return (
    item?.permissions ||
    item?.perms ||
    item?.authorities ||
    meta?.permissions ||
    meta?.perms ||
    meta?.authorities ||
    []
  );
};

const shouldRequireAll = (item) => {
  const meta = getItemMeta(item);

  return (
    item?.requireAll === true ||
    item?.match === "all" ||
    meta?.requireAll === true ||
    meta?.match === "all"
  );
};

const isSuperAdminOnlyItem = (item) => {
  const meta = getItemMeta(item);
  return item?.superAdminOnly === true || meta?.superAdminOnly === true;
};

const itemHasRequiredModule = (user, item) => {
  const requiredModule = getRequiredModule(item);

  if (!requiredModule) return true;

  return hasModule(user, requiredModule);
};

const itemHasRequiredPermission = (user, item) => {
  const requiredPermission = getRequiredPermission(item);
  const requiredPermissions = getRequiredPermissions(item);
  const requireAll = shouldRequireAll(item);

  if (requiredPermission) {
    return hasPermission(user, requiredPermission);
  }

  if (Array.isArray(requiredPermissions) && requiredPermissions.length > 0) {
    return requireAll
      ? hasAllPermissions(user, requiredPermissions)
      : hasAnyPermission(user, requiredPermissions);
  }

  return true;
};

export const canAccessRoute = (user, route) => {
  if (!route) return true;

  const isGlobalAdmin = isGlobalAdminUser(user);

  if (isSuperAdminOnlyItem(route)) {
    return isGlobalAdmin;
  }

  if (isGlobalAdmin) {
    return true;
  }

  if (!itemHasRequiredModule(user, route)) {
    return false;
  }

  if (!itemHasRequiredPermission(user, route)) {
    return false;
  }

  return true;
};

export const filterNavByPerm = (items = [], user) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => {
      if (!item) return false;
      return canAccessRoute(user, item);
    })
    .map((item) => {
      const next = { ...item };

      if (Array.isArray(item.submenu)) {
        next.submenu = filterNavByPerm(item.submenu, user);
      }

      if (Array.isArray(item.children)) {
        next.children = filterNavByPerm(item.children, user);
      }

      const hasSubmenu = Array.isArray(next.submenu) && next.submenu.length > 0;
      const hasChildren =
        Array.isArray(next.children) && next.children.length > 0;

      const isGroupLike =
        item?.type === "group" ||
        item?.type === "collapse" ||
        item?.type === "submenu" ||
        item?.isGroupTitle;

      if (isGroupLike && !hasSubmenu && !hasChildren) {
        return null;
      }

      return next;
    })
    .filter(Boolean);
};